/**
 * Local library album index — lightweight catalog rows for the album wall.
 *
 * Libraries ≤ ALBUM_INDEX_WINDOW_THRESHOLD page in the background after first paint
 * (limit=200, offset+=200 — never a single capped get_albums). Larger libraries enter
 * windowed mode: first chunk paints, additional pages fault in on scroll (#417 / #565).
 */
import { libraryService } from './libraryService';
import { shouldUseLocalLibraryIpc, isLocalCatalogBrowseReady } from './localLibraryIpcGate';
import { isLibraryScanInProgress } from './libraryScanCoordinator';
import {
  callWithLibraryBusyRetry,
  isGetAlbumsTimeoutError,
  isLibraryBusyScanningError,
  isLibraryCatalogRetryableError,
} from '../utils/libraryBusyScanning';
import { awaitCleanScanStatusAfterWebviewRecovery } from '../utils/libraryIndexReloadGate';
import { pollWithStallGuard } from '../utils/pollWithStallGuard';
import {
  extractCatalogBootEnvelope,
  type CatalogBootEnvelope,
} from './catalogBootSnapshot';
import {
  isLocalLibraryAlbumKey,
  normalizeLocalAlbumId,
} from './localLibraryMetadata';
import { matchAlbumRowByPath, matchAlbumRowByTitleArtist } from './libraryAlbumIndexLookup';
import { setGetAlbumsStormTrigger } from './libraryAlbumWallInvalidation';
import {
  advanceAlbumPageCursor, getAlbumPageCursor, isAlbumPagingExhausted, isSamePageRequestBlocked,
  noteAlbumPageRequest, resetAlbumPageCursor, shiftAlbumPageCursorForRemoval,
} from './libraryAlbumPageCursor';
import {
  albumIdentityKey,
  albumRowGridSlice,
  applyChunkDedupe,
  computeAlbumGridFingerprint,
  dedupeAlbumIndexRows,
  getAlbumGridRowKey,
  gridFingerprintDrifted,
  logBornToRunIdentityCounts,
  reconcileAlbumsFromServer,
  resolveCanonicalAlbumTileId,
  type AlbumIndexDedupeResult,
  type AlbumIndexRow,
} from './libraryAlbumIndexDedupe';

export type { AlbumIndexRow } from './libraryAlbumIndexDedupe';
export {
  albumRowGridSlice,
  computeAlbumGridFingerprint,
  dedupeAlbumIndexRows,
  getAlbumGridRowKey,
  gridFingerprintDrifted,
  resolveCanonicalAlbumTileId,
  albumIdentityKey,
} from './libraryAlbumIndexDedupe';

/** First paint chunk — small enough to return while a background scan is running. */
export const ALBUM_INDEX_FIRST_CHUNK = 200;
/** Legacy alias used by tests and callers expecting a single chunk constant. */
export const ALBUM_INDEX_CHUNK_SIZE = ALBUM_INDEX_FIRST_CHUNK;
/** Page size for every get_albums after the first paint (offset += this). */
export const ALBUM_INDEX_PAGE_SIZE = ALBUM_INDEX_FIRST_CHUNK;
/**
 * Above this catalog size, first chunk paints and scroll faults in the rest.
 * Not a fetch ceiling — paging continues past this via loadNextLocalAlbumIndexPage.
 */
export const ALBUM_INDEX_WINDOW_THRESHOLD = 5_000;
/** @deprecated Use ALBUM_INDEX_WINDOW_THRESHOLD — kept so existing imports keep compiling. */
export const ALBUM_INDEX_BULK_CAP = ALBUM_INDEX_WINDOW_THRESHOLD;
/** Fingerprint drift probe — bounded, independent of the window threshold. */
export const ALBUM_INDEX_FINGERPRINT_PROBE_LIMIT = 500;

/** Client-side retries per chunk when get_albums times out or errors (#366 scan-aware backoff). */
export const LOCAL_LIBRARY_GET_ALBUMS_MAX_RETRIES = 3;
export const LOCAL_LIBRARY_GET_ALBUMS_RETRY_BACKOFF_MS = [2000, 4000, 8000] as const;
/** visibility gap that suggests sleep/wake — trigger a fresh index load. */
const WAKE_RESUME_GAP_MS = 30_000;

export type AlbumIndexLoadResult = {
  albums: AlbumIndexRow[];
  total: number;
  bytes: number;
  ms: number;
  complete: boolean;
  error?: string | null;
};

type ChunkHandler = (snapshot: {
  albums: AlbumIndexRow[];
  total: number;
  totalReleases: number;
  bytes: number;
  complete: boolean;
  /** Boot snapshot envelope from latest get_albums page (R-B). */
  catalogBoot?: CatalogBootEnvelope;
}) => void;

/** Opportunistic boot-surface upgrade only — never gates index load. */
function deliverIndexChunk(
  handler: ChunkHandler | undefined,
  snapshot: Parameters<ChunkHandler>[0],
): void {
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('maestro:boot-album-progress', {
        detail: {
          count: snapshot.albums?.length ?? 0,
          total: snapshot.total ?? 0,
          complete: !!snapshot.complete,
        },
      }));
    } catch {
      /* presentational */
    }
  }
  handler?.(snapshot);
}

type DedupeResult = AlbumIndexDedupeResult;

let activeGeneration = 0;
let activeAbort: AbortController | null = null;
/** Completed index load cycles — field log proof the refetch loop is gone. */
let indexLoadCycles = 0;
let inFlightLoad: {
  generation: number;
  promise: Promise<AlbumIndexLoadResult>;
  chunkHandlers: Set<ChunkHandler>;
} | null = null;
let indexComplete = false;
/** Large libraries (> ALBUM_INDEX_WINDOW_THRESHOLD): first chunk only until scroll/on-demand pages. */
let indexWindowed = false;
let indexSort = 'artist';
let pageLoadInFlight: Promise<boolean> | null = null;
let indexAlbums: AlbumIndexRow[] = [];
let indexTotal = 0;
let indexTotalReleases = 0;
let indexBytes = 0;
/** Catalog version + album count the in-memory index was built from (Connector IPC). */
let indexCatalogVersion: number | null = null;
let indexAlbumCount: number | null = null;
/** Sorted album ids + title + cover hashes — grid-relevant fingerprint, not raw catalogVersion. */
let indexGridFingerprint: string | null = null;
let indexLoadError: string | null = null;
const indexLoadErrorListeners = new Set<() => void>();

function notifyIndexLoadErrorListeners(): void {
  indexLoadErrorListeners.forEach((cb) => cb());
}

function setIndexLoadError(message: string | null): void {
  indexLoadError = message;
  notifyIndexLoadErrorListeners();
}

export function getLocalAlbumIndexLoadError(): string | null {
  return indexLoadError;
}

export function clearLocalAlbumIndexLoadError(): void {
  setIndexLoadError(null);
}

export function subscribeLocalAlbumIndexLoadError(cb: () => void): () => void {
  indexLoadErrorListeners.add(cb);
  return () => indexLoadErrorListeners.delete(cb);
}

let wakeRecoveryInstalled = false;

/** After a long hidden gap (sleep/wake), cancel any stuck load and start fresh. */
export function installLocalAlbumIndexWakeRecovery(onResume: () => void): void {
  if (wakeRecoveryInstalled || typeof document === 'undefined') return;
  wakeRecoveryInstalled = true;
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    if (document.visibilityState !== 'visible' || hiddenAt <= 0) return;
    const gapMs = Date.now() - hiddenAt;
    hiddenAt = 0;
    if (gapMs < WAKE_RESUME_GAP_MS) return;
    console.info(
      `[library-grid] app resumed after ${Math.round(gapMs / 1000)}s — restarting album index load`,
    );
    cancelLocalAlbumIndexLoad();
    clearLocalAlbumIndexLoadError();
    onResume();
  });
}

export type LocalAlbumIndexSessionMeta = {
  catalogVersion: number | null;
  albumCount: number | null;
  albumReleaseCount: number | null;
  gridFingerprint: string | null;
  bytes: number;
  complete: boolean;
};

function refreshStoredGridFingerprint(): void {
  indexGridFingerprint = indexAlbums.length ? computeAlbumGridFingerprint(indexAlbums) : null;
}

type DiffEntityItem = {
  entity_type?: string;
  entityType?: string;
  id?: string;
};

function diffEntityType(item: DiffEntityItem): string {
  return String(item.entity_type ?? item.entityType ?? '').trim();
}

export function isLocalAlbumIndexWarm(): boolean {
  return shouldUseLocalLibraryIpc() && indexComplete && indexAlbums.length > 0;
}

export function getLocalAlbumIndexSessionMeta(): LocalAlbumIndexSessionMeta {
  return {
    catalogVersion: indexCatalogVersion,
    albumCount: indexAlbumCount,
    albumReleaseCount: indexTotalReleases || null,
    gridFingerprint: indexGridFingerprint,
    bytes: indexBytes,
    complete: indexComplete,
  };
}

export function setLocalAlbumIndexSessionMeta(
  catalogVersion: number | null,
  albumCount: number | null,
): void {
  if (catalogVersion != null && Number.isFinite(catalogVersion)) {
    indexCatalogVersion = catalogVersion;
  }
  if (albumCount != null && Number.isFinite(albumCount)) {
    indexAlbumCount = albumCount;
  }
}

function estimatePayloadBytes(albums: AlbumIndexRow[]): number {
  try {
    return new TextEncoder().encode(JSON.stringify(albums)).length;
  } catch {
    return albums.length * 400;
  }
}

export function isLocalAlbumIndexComplete(): boolean {
  return indexComplete;
}

export function isLocalAlbumIndexWindowed(): boolean {
  return indexWindowed;
}

export function isLocalAlbumIndexFullyLoaded(): boolean {
  return indexComplete && (!indexWindowed || indexAlbums.length >= indexTotal || isAlbumPagingExhausted(indexTotal));
}

export function hasMoreLocalAlbumIndexPages(): boolean {
  if (!shouldUseLocalLibraryIpc()) return false;
  if (indexTotal <= 0) return false;
  return indexWindowed && !isAlbumPagingExhausted(indexTotal) && !isSamePageRequestBlocked(getAlbumPageCursor());
}

export function getLocalAlbumIndexSnapshot(): {
  albums: AlbumIndexRow[];
  total: number;
  totalReleases: number;
  bytes: number;
} {
  return {
    albums: indexAlbums,
    total: indexTotal,
    totalReleases: indexTotalReleases,
    bytes: indexBytes,
  };
}

/** Scan-time delta commit — does not mark the index complete or refresh the fingerprint. */
export function commitLocalAlbumIndexDelta(
  albums: AlbumIndexRow[],
  total: number,
  version?: number | null,
): void {
  indexAlbums = albums;
  indexTotal = total > 0 ? total : albums.length;
  indexBytes = estimatePayloadBytes(albums);
  if (version != null && Number.isFinite(version) && version > 0) {
    indexCatalogVersion = version;
  }
  indexAlbumCount = indexTotal;
}

/** Look up a loaded index row by album_key / id (bare hash or local:album: URI). */
export function findLocalAlbumIndexRow(albumKey?: string | null): AlbumIndexRow | null {
  const raw = String(albumKey || '').trim();
  if (!raw) return null;
  const bare = raw.replace(/^local:album:/i, '').toLowerCase();
  for (const row of indexAlbums) {
    const key = String(row.album_key ?? row.id ?? '').trim();
    if (!key) continue;
    const rowBare = key.replace(/^local:album:/i, '').toLowerCase();
    if (rowBare === bare || key === raw) return row;
  }
  return null;
}

export function findLocalAlbumIndexRowByPath(
  libraryFolderPath?: string | null,
  sampleFilePath?: string | null,
): AlbumIndexRow | null {
  return matchAlbumRowByPath(indexAlbums, libraryFolderPath, sampleFilePath);
}

export function findLocalAlbumIndexRowByTitleArtist(
  title?: string | null,
  artist?: string | null,
): AlbumIndexRow | null {
  return matchAlbumRowByTitleArtist(indexAlbums, title, artist);
}

/** Drop one stale/ghost tile from the in-memory index without clearing scroll prefs. */
export function removeAlbumFromLocalIndex(albumKey?: string | null): boolean {
  const raw = String(albumKey || '').trim();
  if (!raw) return false;
  const bare = raw.replace(/^local:album:/i, '').toLowerCase();
  const before = indexAlbums.length;
  indexAlbums = indexAlbums.filter((row) => {
    const key = String(row.album_key ?? row.id ?? '').trim();
    if (!key) return true;
    const rowBare = key.replace(/^local:album:/i, '').toLowerCase();
    return rowBare !== bare && key !== raw;
  });
  if (indexAlbums.length === before) return false;
  indexTotal = Math.max(indexAlbums.length, indexTotal - 1);
  shiftAlbumPageCursorForRemoval();
  indexAlbumCount = indexAlbums.length;
  indexBytes = estimatePayloadBytes(indexAlbums);
  refreshStoredGridFingerprint();
  return true;
}

export function getLocalAlbumIndexLoadCycles(): number {
  return indexLoadCycles;
}

export function resetLocalAlbumIndex(): void {
  activeGeneration += 1;
  activeAbort?.abort();
  activeAbort = null;
  inFlightLoad = null;
  pageLoadInFlight = null;
  indexComplete = false;
  indexWindowed = false;
  resetAlbumPageCursor();
  indexSort = 'artist';
  indexAlbums = [];
  indexTotal = 0;
  indexTotalReleases = 0;
  indexBytes = 0;
  indexCatalogVersion = null;
  indexAlbumCount = null;
  indexGridFingerprint = null;
}

/** Test helper — seed a warm in-memory index without IPC. */
export function installLocalAlbumIndexRowsForTest(
  albums: AlbumIndexRow[],
  options: { total?: number; catalogVersion?: number } = {},
): void {
  indexAlbums = albums.slice();
  indexTotal = options.total ?? albums.length;
  indexAlbumCount = indexTotal;
  indexComplete = true;
  indexWindowed = false;
  indexCatalogVersion = options.catalogVersion ?? null;
  refreshStoredGridFingerprint();
}

/** Destructive catalog wipe/remove — cold grid refresh (mirrors wipe + scan-started invalidation). */
export function dispatchLocalAlbumCatalogCleared(): void {
  resetLocalAlbumIndex();
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('maestro:library:changed', {
      detail: { forceFullRefresh: true, reason: 'catalog-cleared' },
    }));
  } catch {
    /* ignore */
  }
}

export function cancelLocalAlbumIndexLoad(): void {
  activeGeneration += 1;
  activeAbort?.abort();
  activeAbort = null;
  inFlightLoad = null;
}

function deliverIndexChunkToHandlers(
  handlers: Set<ChunkHandler>,
  snapshot: Parameters<ChunkHandler>[0],
): void {
  for (const handler of handlers) {
    deliverIndexChunk(handler, snapshot);
  }
}

type AlbumListFetchArgs = {
  sort: string;
  limit: number;
  offset: number;
};

async function fetchAlbumsPageWithRetry(
  args: AlbumListFetchArgs,
  signal: AbortSignal,
): Promise<Awaited<ReturnType<typeof libraryService.getAlbums>>> {
  return callWithLibraryBusyRetry(
    () => libraryService.getAlbums(args),
    { signal },
  );
}

function mergeIndexPage(
  page: AlbumIndexRow[],
  offset: number,
): { albums: AlbumIndexRow[]; collapsed: number } {
  let accumulated: AlbumIndexRow[];
  if (offset === 0) {
    accumulated = page.slice();
  } else {
    accumulated = indexAlbums.concat(page);
  }
  const { albums, collapsed } = applyChunkDedupe(accumulated);
  indexAlbums = albums;
  indexBytes = estimatePayloadBytes(albums);
  return { albums, collapsed };
}

/**
 * Fetch the next page (limit=ALBUM_INDEX_PAGE_SIZE). No-op when the index is fully loaded.
 */
export async function loadNextLocalAlbumIndexPage(options: {
  sort?: string;
  onChunk?: ChunkHandler;
} = {}): Promise<boolean> {
  if (!shouldUseLocalLibraryIpc() || !hasMoreLocalAlbumIndexPages()) return false;

  if (pageLoadInFlight) {
    await pageLoadInFlight;
    return hasMoreLocalAlbumIndexPages();
  }

  const sort = options.sort ?? indexSort ?? 'artist';
  const offset = getAlbumPageCursor();

  pageLoadInFlight = (async () => {
    if (!activeAbort || activeAbort.signal.aborted) {
      activeAbort = new AbortController();
    }
    const { signal } = activeAbort;

    try {
      if (!noteAlbumPageRequest({ offset, limit: ALBUM_INDEX_PAGE_SIZE, total: indexTotal })) return false;
      const resp = await fetchAlbumsPageWithRetry({
        sort,
        limit: ALBUM_INDEX_PAGE_SIZE,
        offset,
      }, signal);
      if (signal.aborted) return false;

      const page = (resp?.albums || []) as AlbumIndexRow[];
      const catalogBoot = extractCatalogBootEnvelope(
        resp as Record<string, unknown> | null | undefined,
      );
      const pageTotal = Number(resp?.total_count ?? resp?.total ?? indexTotal ?? page.length);
      if (pageTotal > 0) indexTotal = pageTotal;
      const pageReleases = Number(
        (resp as { total_releases?: number })?.total_releases ?? 0,
      );
      if (Number.isFinite(pageReleases) && pageReleases > 0) {
        indexTotalReleases = pageReleases;
      }

      advanceAlbumPageCursor(page.length);
      const { albums } = mergeIndexPage(page, offset);
      const complete = isAlbumPagingExhausted(indexTotal);

      deliverIndexChunk(options.onChunk, {
        albums,
        total: indexTotal,
        totalReleases: indexTotalReleases,
        bytes: indexBytes,
        complete,
        catalogBoot,
      });

      if (complete) {
        indexComplete = true;
        refreshStoredGridFingerprint();
      }

      return hasMoreLocalAlbumIndexPages();
    } catch (e) {
      if (!signal.aborted && !isLibraryCatalogRetryableError(e)) {
        console.warn('[library-grid] windowed page load failed:', e);
      }
      return hasMoreLocalAlbumIndexPages();
    } finally {
      pageLoadInFlight = null;
    }
  })();

  return pageLoadInFlight;
}

/**
 * Load the full local album index in large background chunks. Safe to call
 * multiple times — concurrent callers join the in-flight load (single-flight).
 * Explicit resetLocalAlbumIndex / cancelLocalAlbumIndexLoad start fresh.
 */
export async function loadLocalAlbumIndex(options: {
  sort?: string;
  onChunk?: ChunkHandler;
  /** When true, reconcile the first chunk against existing rows (scan-time refresh). */
  mergeFirstChunk?: boolean;
  existing?: AlbumIndexRow[];
} = {}): Promise<AlbumIndexLoadResult> {
  if (!shouldUseLocalLibraryIpc()) {
    return { albums: [], total: 0, bytes: 0, ms: 0, complete: true };
  }

  const sort = options.sort ?? 'artist';
  if (
    !options.mergeFirstChunk
    && isLocalAlbumIndexWarm()
    && sort === indexSort
    && !inFlightLoad
  ) {
    if (options.onChunk) {
      deliverIndexChunk(options.onChunk, {
        albums: indexAlbums,
        total: indexTotal,
        totalReleases: indexTotalReleases,
        bytes: indexBytes,
        complete: isLocalAlbumIndexFullyLoaded(),
      });
    }
    return {
      albums: indexAlbums,
      total: indexTotal,
      bytes: indexBytes,
      ms: 0,
      complete: indexComplete,
    };
  }

  if (
    inFlightLoad
    && inFlightLoad.generation === activeGeneration
  ) {
    if (options.onChunk) inFlightLoad.chunkHandlers.add(options.onChunk);
    return inFlightLoad.promise;
  }

  const generation = activeGeneration;
  const chunkHandlers = new Set<ChunkHandler>();
  if (options.onChunk) chunkHandlers.add(options.onChunk);

  const promise = executeLocalAlbumIndexLoad(options, generation, chunkHandlers);
  inFlightLoad = { generation, promise, chunkHandlers };

  try {
    return await promise;
  } finally {
    if (inFlightLoad?.promise === promise) inFlightLoad = null;
  }
}

async function executeLocalAlbumIndexLoad(
  options: {
    sort?: string;
    mergeFirstChunk?: boolean;
    existing?: AlbumIndexRow[];
  },
  generation: number,
  chunkHandlers: Set<ChunkHandler>,
): Promise<AlbumIndexLoadResult> {
  const started = performance.now();
  const sort = options.sort ?? 'artist';

  if (!activeAbort || activeAbort.signal.aborted) {
    activeAbort = new AbortController();
  }
  const { signal } = activeAbort;

  setIndexLoadError(null);

  const recoveryGate = await awaitCleanScanStatusAfterWebviewRecovery(signal);
  if (recoveryGate === 'defer_scan_active') {
    if (signal.aborted || generation !== activeGeneration) {
      return {
        albums: indexAlbums,
        total: indexTotal,
        bytes: indexBytes,
        ms: Math.round(performance.now() - started),
        complete: false,
      };
    }
    deliverIndexChunkToHandlers(chunkHandlers, {
      albums: indexAlbums.length ? indexAlbums : (options.existing ?? []),
      total: indexTotal,
      totalReleases: indexTotalReleases,
      bytes: indexBytes,
      complete: false,
    });
    return {
      albums: indexAlbums,
      total: indexTotal,
      bytes: indexBytes,
      ms: Math.round(performance.now() - started),
      complete: false,
    };
  }

  const existingDeduped = options.existing?.length
    ? dedupeAlbumIndexRows(options.existing).albums
    : undefined;

  if (!options.mergeFirstChunk) {
    indexComplete = false;
    indexWindowed = false;
  }

  indexSort = sort;
  let accumulated: AlbumIndexRow[] = [];
  let serverTotal = 0;
  let offset = 0;
  let totalCollapsed = 0;
  let lastRawBeforeDedupe = 0;
  let lastRawAlbums: AlbumIndexRow[] = [];
  let loadError: string | null = null;

  try {
    for (;;) {
      if (signal.aborted || generation !== activeGeneration) break;

      const resp = await fetchAlbumsPageWithRetry({
        sort,
        limit: ALBUM_INDEX_PAGE_SIZE,
        offset,
      }, signal);
      if (signal.aborted || generation !== activeGeneration) break;

      const page = (resp?.albums || []) as AlbumIndexRow[];
      const catalogBoot = extractCatalogBootEnvelope(
        resp as Record<string, unknown> | null | undefined,
      );
      if (offset === 0) {
        const pageVersion = Number((resp as { version?: number })?.version ?? 0);
        if (pageVersion > 0) indexCatalogVersion = pageVersion;
      }
      serverTotal = Number(resp?.total_count ?? resp?.total ?? serverTotal ?? page.length);
      const pageReleases = Number(
        (resp as { total_releases?: number })?.total_releases ?? 0,
      );
      if (Number.isFinite(pageReleases) && pageReleases > 0) {
        indexTotalReleases = pageReleases;
      }

      if (offset === 0 && options.mergeFirstChunk && existingDeduped?.length) {
        accumulated = reconcileAlbumsFromServer(existingDeduped, page);
      } else if (offset === 0) {
        accumulated = page.slice();
      } else {
        accumulated = accumulated.concat(page);
      }

      lastRawBeforeDedupe = accumulated.length;
      lastRawAlbums = accumulated;
      const { albums: deduped, collapsed } = applyChunkDedupe(accumulated);
      totalCollapsed += collapsed;
      accumulated = deduped;

      indexAlbums = accumulated;
      indexTotal = serverTotal || accumulated.length;
      indexBytes = estimatePayloadBytes(accumulated);

      const fetchedAll = page.length === 0
        || accumulated.length >= indexTotal;

      if (fetchedAll && indexTotal === 0 && indexAlbums.length === 0 && shouldUseLocalLibraryIpc()) {
        if (!isLocalCatalogBrowseReady()) {
          deliverIndexChunkToHandlers(chunkHandlers, {
            albums: accumulated,
            total: indexTotal,
            totalReleases: indexTotalReleases,
            bytes: indexBytes,
            complete: false,
            catalogBoot,
          });
          break;
        }
        try {
          const stats = await libraryService.getStats();
          const statsAlbums = Number(stats?.albums ?? stats?.total_albums ?? -1);
          if (Number.isFinite(statsAlbums) && statsAlbums > 0) {
            deliverIndexChunkToHandlers(chunkHandlers, {
              albums: accumulated,
              total: indexTotal,
              totalReleases: indexTotalReleases,
              bytes: indexBytes,
              complete: false,
              catalogBoot,
            });
            break;
          }
        } catch {
          deliverIndexChunkToHandlers(chunkHandlers, {
            albums: accumulated,
            total: indexTotal,
            totalReleases: indexTotalReleases,
            bytes: indexBytes,
            complete: false,
            catalogBoot,
          });
          break;
        }
      }

      // #417 / #565 — huge libraries: first chunk paints; scroll faults in the rest.
      if (
        offset === 0
        && indexTotal > ALBUM_INDEX_WINDOW_THRESHOLD
        && indexTotal > page.length
      ) {
        indexWindowed = true;
        indexComplete = true;
        resetAlbumPageCursor(page.length);
        deliverIndexChunkToHandlers(chunkHandlers, {
          albums: accumulated,
          total: indexTotal,
          totalReleases: indexTotalReleases,
          bytes: indexBytes,
          complete: true,
          catalogBoot,
        });
        console.log(
          `[library-grid] index windowed n=${accumulated.length} total=${indexTotal} — scroll loads pages`,
        );
        break;
      }

      let complete = fetchedAll || page.length < ALBUM_INDEX_PAGE_SIZE;
      const deferForActiveScan = complete
        && indexTotal === 0
        && indexAlbums.length === 0
        && isLibraryScanInProgress();
      if (deferForActiveScan) {
        complete = false;
        indexComplete = false;
      }

      deliverIndexChunkToHandlers(chunkHandlers, {
        albums: accumulated,
        total: indexTotal,
        totalReleases: indexTotalReleases,
        bytes: indexBytes,
        complete,
        catalogBoot,
      });

      if (complete) {
        indexComplete = true;
        break;
      }

      // Empty page or scan deferral — exit; scan poll (≤2s) will re-invoke.
      if (page.length === 0 || deferForActiveScan) {
        break;
      }

      offset += page.length;
    }
  } catch (e) {
    if (!signal.aborted) {
      const message = e instanceof Error ? e.message : String(e);
      if (!isLibraryCatalogRetryableError(e)) {
        loadError = message || 'Library catalog load failed';
        setIndexLoadError(loadError);
        console.warn('[library-grid] index load failed:', e);
      } else if (isGetAlbumsTimeoutError(e)) {
        console.info('[library-grid] index load deferred — get_albums timeout (scan-aware backoff)');
      }
      deliverIndexChunkToHandlers(chunkHandlers, {
        albums: indexAlbums.length ? indexAlbums : accumulated,
        total: indexTotal || serverTotal || indexAlbums.length,
        totalReleases: indexTotalReleases,
        bytes: indexBytes,
        complete: false,
      });
    }
  }

  const ms = Math.round(performance.now() - started);
  const complete = indexComplete && generation === activeGeneration && !loadError;

  if (complete) {
    indexAlbumCount = indexTotal || indexAlbums.length;
    refreshStoredGridFingerprint();
    indexLoadCycles += 1;
    const rawCount = lastRawBeforeDedupe || indexAlbums.length + totalCollapsed;
    console.log(
      `[library-grid] index loaded n=${rawCount} unique=${indexAlbums.length} bytes=${indexBytes} ms=${ms} catalogVersion=${indexCatalogVersion ?? 'n/a'} fingerprint=${indexGridFingerprint ?? 'n/a'}`,
    );
    if (indexTotal > 0 && indexComplete) {
      console.log(`[library-grid] index settled n=${indexAlbums.length} loads=${indexLoadCycles}`);
    }
    if (totalCollapsed > 0) {
      console.log(`[library-grid] index dedupe collapsed=${totalCollapsed} duplicate rows`);
      logBornToRunIdentityCounts(lastRawAlbums);
    }
  }

  return {
    albums: indexAlbums,
    total: indexTotal,
    bytes: indexBytes,
    ms,
    complete,
    error: loadError,
  };
}

/** Wait until the in-memory index contains at least `targetCount` rows (scroll restore). */
export async function waitForLocalAlbumIndexDepth(
  targetCount: number,
  options: { sort?: string; onChunk?: ChunkHandler; existing?: AlbumIndexRow[] } = {},
): Promise<number> {
  const target = Math.max(0, Number(targetCount) || 0);
  if (!shouldUseLocalLibraryIpc()) return indexAlbums.length;
  if (indexAlbums.length >= target && isLocalAlbumIndexFullyLoaded()) return indexAlbums.length;
  if (indexAlbums.length >= target && indexWindowed) return indexAlbums.length;

  const existing = options.existing?.length
    ? dedupeAlbumIndexRows(options.existing).albums
    : dedupeAlbumIndexRows(indexAlbums).albums;

  if (!indexWindowed && !indexComplete) {
    await loadLocalAlbumIndex({
      sort: options.sort,
      onChunk: options.onChunk,
      mergeFirstChunk: !!existing.length,
      existing,
    });
    if (indexAlbums.length >= target || isLocalAlbumIndexFullyLoaded()) {
      return indexAlbums.length;
    }
  }

  await pollWithStallGuard(
    () => indexAlbums.length < target && hasMoreLocalAlbumIndexPages(),
    () => indexAlbums.length,
    () => loadNextLocalAlbumIndexPage({ sort: options.sort, onChunk: options.onChunk }),
  );

  return indexAlbums.length;
}

/** Dedupe scroll-restore or other hydrated rows before they enter React state. */
export function dedupeAlbumRowsForGrid(albums: AlbumIndexRow[]): AlbumIndexRow[] {
  return dedupeAlbumIndexRows(albums).albums;
}

export type LocalAlbumSessionRevalidateResult = {
  status: 'unchanged' | 'merged' | 'reloaded';
  reason: string;
};

function albumIdsFromDiffItems(items: DiffEntityItem[]): string[] {
  const ids: string[] = [];
  for (const item of items) {
    if (diffEntityType(item) !== 'album') continue;
    const id = String(item.id ?? '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

function pruneAlbumRowsByIds(albums: AlbumIndexRow[], removedIds: string[]): AlbumIndexRow[] {
  if (!removedIds.length) return albums;
  const removed = new Set(removedIds);
  const alsoBare = new Set(removedIds.map((id) => normalizeLocalAlbumId(id)).filter(Boolean));
  return albums.filter((row) => {
    const key = String(row.album_key ?? row.id ?? '').trim();
    if (!key) return true;
    return !removed.has(key) && !alsoBare.has(key) && !alsoBare.has(normalizeLocalAlbumId(key));
  });
}

function commitPatchedIndex(
  albums: AlbumIndexRow[],
  onMerge?: (snapshot: { albums: AlbumIndexRow[]; total: number; totalReleases: number; bytes: number }) => void,
): void {
  indexAlbums = dedupeAlbumIndexRows(albums).albums;
  indexBytes = estimatePayloadBytes(indexAlbums);
  refreshStoredGridFingerprint();
  onMerge?.({
    albums: indexAlbums,
    total: indexTotal,
    totalReleases: indexTotalReleases,
    bytes: indexBytes,
  });
}

/**
 * Refetch only the dirty album id(s) and patch the in-memory wall.
 * Never pages the corpus. One edit → one getAlbum per id.
 */
export async function patchLocalAlbumIndexAlbums(
  albumIds: string[],
  options: {
    onMerge?: (snapshot: { albums: AlbumIndexRow[]; total: number; totalReleases: number; bytes: number }) => void;
  } = {},
): Promise<{ updatedCount: number }> {
  const ids = (albumIds || []).map((id) => String(id || '').trim()).filter(Boolean);
  if (!ids.length || !indexAlbums.length) return { updatedCount: 0 };
  const items: DiffEntityItem[] = ids.map((id) => ({ entity_type: 'album', id }));
  const merged = await mergeGridRelevantAlbumUpdates(items, indexAlbums);
  if (merged.changed) {
    commitPatchedIndex(merged.albums, options.onMerge);
  }
  return { updatedCount: merged.updatedCount };
}

async function mergeGridRelevantAlbumUpdates(
  items: DiffEntityItem[],
  existing: AlbumIndexRow[],
): Promise<{ changed: boolean; albums: AlbumIndexRow[]; updatedCount: number }> {
  let albums = existing;
  let changed = false;
  let updatedCount = 0;
  const byStableId = new Map<string, AlbumIndexRow>();
  const remember = (row: AlbumIndexRow) => {
    const sid = String(row.album_key ?? row.id ?? '').trim();
    if (sid) byStableId.set(sid, row);
    const bare = normalizeLocalAlbumId(sid);
    if (bare) byStableId.set(bare, row);
  };
  for (const row of existing) remember(row);

  const findPrior = (sid: string): AlbumIndexRow | undefined => {
    if (byStableId.has(sid)) return byStableId.get(sid);
    const bare = normalizeLocalAlbumId(sid);
    if (bare && byStableId.has(bare)) return byStableId.get(bare);
    return undefined;
  };

  for (const item of items) {
    const sid = String(item.id ?? '').trim();
    if (!sid) continue;
    const prior = findPrior(sid);
    try {
      const resp = await libraryService.getAlbum(sid);
      const fresh = (resp as { album?: AlbumIndexRow })?.album;
      if (!fresh) continue;
      if (prior && albumRowGridSlice(prior) === albumRowGridSlice(fresh)) continue;
      changed = true;
      updatedCount += 1;
      if (!prior) {
        albums = albums.concat(fresh);
        remember(fresh);
        continue;
      }
      const key = albumIdentityKey(prior);
      albums = albums.map((a) => (albumIdentityKey(a) === key ? { ...a, ...fresh } : a));
      remember({ ...prior, ...fresh });
    } catch {
      /* keep cached row */
    }
  }

  if (changed) {
    albums = dedupeAlbumIndexRows(albums).albums;
  }
  return { changed, albums, updatedCount };
}

async function runBackgroundIndexMerge(
  onMerge?: (snapshot: { albums: AlbumIndexRow[]; total: number; totalReleases: number; bytes: number }) => void,
): Promise<void> {
  const existing = indexAlbums.slice();
  await loadLocalAlbumIndex({
    sort: 'artist',
    mergeFirstChunk: existing.length > 0,
    existing,
    onChunk: (snapshot) => {
      onMerge?.({
        albums: snapshot.albums,
        total: snapshot.total,
        totalReleases: snapshot.totalReleases,
        bytes: snapshot.bytes,
      });
    },
  });
  refreshStoredGridFingerprint();
}

async function fetchCanonicalAlbumTotalFromServer(): Promise<number | null> {
  try {
    const resp = await callWithLibraryBusyRetry(() => libraryService.getAlbums({
      limit: 1,
      offset: 0,
      sort: 'artist',
    }));
    const total = Number(resp?.total_count ?? resp?.total);
    return Number.isFinite(total) && total >= 0 ? total : null;
  } catch (e) {
    if (isLibraryBusyScanningError(e)) return null;
    return null;
  }
}

/**
 * Warm-session background check — never blocks paint. Ignores catalogVersion-only
 * drift; reloads only when canonical album tile count or grid fingerprint changes.
 */
export async function revalidateLocalAlbumSession(options: {
  onMerge?: (snapshot: { albums: AlbumIndexRow[]; total: number; totalReleases: number; bytes: number }) => void;
} = {}): Promise<LocalAlbumSessionRevalidateResult> {
  if (!isLocalAlbumIndexWarm()) {
    return { status: 'unchanged', reason: 'cold' };
  }

  const meta = getLocalAlbumIndexSessionMeta();
  const started = performance.now();

  let statsResp: Awaited<ReturnType<typeof libraryService.getStats>> | null = null;
  try {
    statsResp = await callWithLibraryBusyRetry(() => libraryService.getStats());
  } catch (e) {
    if (isLibraryBusyScanningError(e)) {
      return { status: 'unchanged', reason: 'stats-busy' };
    }
    console.warn('[library-grid] session revalidate stats failed — keeping warm cache', e);
    return { status: 'unchanged', reason: 'stats-failed' };
  }

  const serverCanonical = await fetchCanonicalAlbumTotalFromServer();
  const countChanged = serverCanonical != null
    && meta.albumCount != null
    && serverCanonical !== meta.albumCount;

  const sinceVersion = meta.catalogVersion;
  let diff: Awaited<ReturnType<typeof libraryService.getLibraryDiff>> | null = null;
  if (sinceVersion != null && Number.isFinite(sinceVersion)) {
    try {
      diff = await callWithLibraryBusyRetry(() => libraryService.getLibraryDiff(String(sinceVersion)));
    } catch (e) {
      if (isLibraryBusyScanningError(e)) {
        return { status: 'unchanged', reason: 'diff-busy' };
      }
      console.warn('[library-grid] session revalidate diff failed — keeping warm cache', e);
    }
  }

  const newVersion = Number(diff?.to_version ?? 0) || null;
  if (newVersion && newVersion !== sinceVersion) {
    setLocalAlbumIndexSessionMeta(newVersion, meta.albumCount);
  } else if (
    newVersion
    && newVersion === sinceVersion
    && !countChanged
  ) {
    try {
      const probe = await callWithLibraryBusyRetry(() => libraryService.getAlbums({
        sort: 'artist',
        limit: ALBUM_INDEX_FINGERPRINT_PROBE_LIMIT,
        offset: 0,
      }));
      const freshRows = dedupeAlbumRowsForGrid((probe?.albums || []) as AlbumIndexRow[]);
      if (gridFingerprintDrifted(meta.gridFingerprint, freshRows)) {
        console.log('[library-grid] fingerprint drift at stable count — reload required', {
          warmFingerprint: meta.gridFingerprint,
          probeCount: freshRows.length,
        });
        return { status: 'reloaded', reason: 'fingerprint-drift' };
      }
    } catch (e) {
      if (isLibraryBusyScanningError(e)) {
        return { status: 'unchanged', reason: 'fingerprint-probe-busy' };
      }
      console.warn('[library-grid] fingerprint drift probe failed — keeping warm cache', e);
    }
    console.log('[library-grid] library fetch end (session cache hit)', {
      ms: Math.round(performance.now() - started),
      albumCount: indexAlbums.length,
      catalogVersion: newVersion,
      fingerprint: meta.gridFingerprint,
      reason: 'catalog-version-unchanged',
    });
    return { status: 'unchanged', reason: 'catalog-version-unchanged' };
  }

  const added = (diff?.added || []) as DiffEntityItem[];
  const updated = ((diff as { modified?: DiffEntityItem[] } | null)?.modified || []) as DiffEntityItem[];
  const removed = (diff?.removed || []) as DiffEntityItem[];
  const albumAdds = albumIdsFromDiffItems(added);
  const albumRems = albumIdsFromDiffItems(removed);
  const albumUpdates = updated.filter((i) => diffEntityType(i) === 'album');

  if (albumRems.length) {
    commitPatchedIndex(pruneAlbumRowsByIds(indexAlbums, albumRems), options.onMerge);
    indexTotal = serverCanonical ?? indexAlbums.length;
    indexAlbumCount = indexTotal;
  }

  const dirtyIds = [...albumAdds, ...albumUpdates.map((i) => String(i.id ?? '').trim()).filter(Boolean)];
  if (dirtyIds.length) {
    setGetAlbumsStormTrigger('album-id-patch');
    const merged = await mergeGridRelevantAlbumUpdates(
      dirtyIds.map((id) => ({ entity_type: 'album', id })),
      indexAlbums,
    );
    if (merged.changed) {
      commitPatchedIndex(merged.albums, options.onMerge);
    }
    if (serverCanonical != null) {
      indexTotal = serverCanonical;
      setLocalAlbumIndexSessionMeta(newVersion ?? sinceVersion, serverCanonical);
    }
    console.log('[library-grid] grid merged in place', {
      ms: Math.round(performance.now() - started),
      added: albumAdds.length,
      removed: albumRems.length,
      updatedTiles: merged.updatedCount,
      fingerprint: indexGridFingerprint,
    });
    return {
      status: 'merged',
      reason: albumAdds.length || albumRems.length ? 'album-add-remove-delta' : 'album-grid-delta',
    };
  }

  if (countChanged && serverCanonical != null && serverCanonical > (meta.albumCount ?? 0)) {
    const want = serverCanonical - (meta.albumCount ?? 0);
    const pageLimit = Math.min(ALBUM_INDEX_PAGE_SIZE, Math.max(want, 1));
    setGetAlbumsStormTrigger('scan-count-delta');
    const page = await callWithLibraryBusyRetry(() => libraryService.getAlbums({
      sort: 'artist',
      limit: pageLimit,
      offset: Math.max(0, meta.albumCount ?? indexAlbums.length),
    }));
    const incoming = (page?.albums || []) as AlbumIndexRow[];
    if (incoming.length) {
      const merged = await mergeGridRelevantAlbumUpdates(
        incoming.map((row) => ({ entity_type: 'album', id: String(row.album_key ?? row.id ?? '') })),
        indexAlbums,
      );
      const next = merged.changed ? merged.albums : indexAlbums.concat(incoming);
      commitPatchedIndex(next, options.onMerge);
    }
    indexTotal = serverCanonical;
    setLocalAlbumIndexSessionMeta(newVersion ?? sinceVersion, serverCanonical);
    console.log('[library-grid] scan delta merge', {
      appended: want,
      total: serverCanonical,
      fetchLimit: pageLimit,
    });
    return { status: 'merged', reason: 'album-count-delta' };
  }

  if (countChanged && serverCanonical != null) {
    indexTotal = serverCanonical;
    setLocalAlbumIndexSessionMeta(newVersion ?? sinceVersion, serverCanonical);
  }

  console.log('[library-grid] library fetch end (session cache hit)', {
    ms: Math.round(performance.now() - started),
    albumCount: indexAlbums.length,
    catalogVersion: newVersion ?? sinceVersion,
    fingerprint: meta.gridFingerprint,
    reason: countChanged ? 'count-adjusted' : 'non-grid-catalog-changes',
    indexedReleases: statsResp?.albums ?? null,
  });
  return { status: 'unchanged', reason: countChanged ? 'count-adjusted' : 'non-grid-changes' };
}
